import React, { useState, useEffect } from 'react';
import { FileText, Calendar, Phone, MapPin, Building, Tag, Package, Target, Monitor, Upload, X, CheckCircle, AlertCircle, Plus, Download } from 'lucide-react';
import { useTheme } from './ThemeContext';
import { useAuth } from '../auth/AuthContext';
import { COMPLETE_TAXONOMY_DATA, getSubcategoriesForCategory, getAdProductForSelection } from '../data/TaxonomyData';
import { TERMS_AND_CONDITIONS } from '../data/TermsAndConditions';
import InventoryPicker from './InventoryPicker';

// Parse an MP4/ISO-BMFF file's raw bytes to find moov > trak > tkhd width/height.
// Unlike the HTML5 <video> element, this works no matter where the moov atom sits
// in the file (front or end -- i.e. files exported without "faststart"), because
// we already have the whole file locally and can just walk the box structure.
function parseIsoBmffDimensions(buffer) {
  const dv = new DataView(buffer);
  const len = buffer.byteLength;

  function readBoxes(start, end) {
    const boxes = [];
    let pos = start;
    while (pos + 8 <= end) {
      let size = dv.getUint32(pos);
      const type = String.fromCharCode(
        dv.getUint8(pos + 4), dv.getUint8(pos + 5), dv.getUint8(pos + 6), dv.getUint8(pos + 7)
      );
      let headerSize = 8;
      if (size === 1) {
        // 64-bit extended size
        if (pos + 16 > end) break;
        const high = dv.getUint32(pos + 8);
        const low = dv.getUint32(pos + 12);
        size = high * 4294967296 + low;
        headerSize = 16;
      } else if (size === 0) {
        // Box extends to the end of its parent
        size = end - pos;
      }
      if (size < headerSize || pos + size > end) break;
      boxes.push({ type, start: pos, headerSize, size });
      pos += size;
    }
    return boxes;
  }

  const findBox = (boxes, type) => boxes.find((b) => b.type === type);

  try {
    const moov = findBox(readBoxes(0, len), 'moov');
    if (!moov) return null;

    const traks = readBoxes(moov.start + moov.headerSize, moov.start + moov.size)
      .filter((b) => b.type === 'trak');

    for (const trak of traks) {
      const tkhd = findBox(
        readBoxes(trak.start + trak.headerSize, trak.start + trak.size),
        'tkhd'
      );
      if (!tkhd) continue;
      // width/height are always the last 8 bytes of tkhd (4 bytes each,
      // 16.16 fixed-point), regardless of tkhd version 0 vs 1.
      const boxEnd = tkhd.start + tkhd.size;
      const width = dv.getUint32(boxEnd - 8) / 65536;
      const height = dv.getUint32(boxEnd - 4) / 65536;
      // Audio/other non-visual tracks report 0x0; skip those.
      if (width > 0 && height > 0) {
        return { width: Math.round(width), height: Math.round(height) };
      }
    }
    return null;
  } catch (_) {
    return null;
  }
}

// Campaign Formats and Display Targets - ALPHABETICALLY SORTED
const CAMPAIGN_FORMATS = [
  { value: 'iab-standard', label: 'IAB Standard (Websites)' },
  { value: 'image', label: 'Image' },
  { value: 'mobile', label: 'Mobile' },
  { value: 'video', label: 'Video' }
];

const DISPLAY_TARGETS = {
  image: [
    { value: 'digital-signage-1080x1920-portrait', label: 'Digital Signage 1080x1920 (portrait)' },
    { value: 'digital-signage-1920x1080-landscape', label: 'Digital Signage 1920x1080 (landscape)' },
    { value: 'faucet-signs-1920x1080', label: 'Faucet Signs 1920x1080' },
    { value: 'slot-service-window-320x320', label: 'Slot Service Window 320x320' },
    { value: 'slot-service-window-640x240', label: 'Slot Service Window 640x240' },
    { value: 'table-game-signs-1920x1080', label: 'Table Game Signs 1920x1080' }
  ],
  video: [
    { value: 'digital-signage-1080x1920-portrait', label: 'Digital Signage 1080x1920 (portrait)' },
    { value: 'digital-signage-1920x1080-landscape', label: 'Digital Signage 1920x1080 (landscape)' },
    { value: 'faucet-signs-1920x1080', label: 'Faucet Signs 1920x1080' },
    { value: 'slot-service-window-320x320', label: 'Slot Service Window 320x320' },
    { value: 'slot-service-window-640x240', label: 'Slot Service Window 640x240' },
    { value: 'table-game-signs-1920x1080', label: 'Table Game Signs 1920x1080' }
  ],
  'iab-standard': [
    { value: 'billboard', label: 'Billboard' },
    { value: 'leaderboard', label: 'Leaderboard' },
    { value: 'medium-rectangle', label: 'Medium Rectangle' },
    { value: 'portrait', label: 'Portrait' },
    { value: 'skyscraper', label: 'Skyscraper' },
    { value: 'super-leaderboard-pushdown', label: 'Super Leaderboard/ Pushdown' }
  ],
  mobile: [
    { value: 'casino-app', label: 'Casino App' },
    { value: 'concierge-app', label: 'Concierge App' },
    { value: 'feature-phone-large-banner', label: 'Feature Phone Large Banner' },
    { value: 'feature-phone-medium-banner', label: 'Feature Phone Medium Banner' },
    { value: 'feature-phone-small-banner', label: 'Feature Phone Small Banner' },
    { value: 'sportsbook-app', label: 'Sportsbook App' }
  ]
};

// PMP Target Options
const PMP_TARGETS = {
  casino: {
    label: 'Casino',
    disabled: true,
    pmps: []
  },
  routeOperations: {
    label: 'Route Operations',
    disabled: false,
    pmps: [
      { value: 'river-city-amusements', label: 'River City Amusements - Milwaukee, WI' }
    ]
  },
  onlineMobile: {
    label: 'Online/Mobile Apps',
    disabled: true,
    pmps: []
  }
};

// Campaign Term Options
const CAMPAIGN_TERMS = [
  { value: '3-month', label: '3-Month Commitment', months: 3 },
  { value: '6-month', label: '6-Month Commitment', months: 6 },
  { value: 'annual', label: 'Annual Commitment', months: 12 }
];

// Campaign Type Options
const CAMPAIGN_TYPES = [
  { value: 'venue-promotion', label: 'Venue promotion (general brand awareness)' },
  { value: 'event-promotion', label: 'Specific event promotion' },
  { value: 'food-beverage', label: 'Food & beverage special' },
  { value: 'sponsor-partner', label: 'Sponsor/partner content' },
  { value: 'community-message', label: 'Community message/cause' },
  { value: 'operational', label: 'Operational announcement (hours, policies)' }
];

// Campaign Timing Options
const CAMPAIGN_TIMING = [
  { value: 'continuous', label: 'Continuously during campaign dates' },
  { value: 'specific-hours', label: 'Specific hours only' },
  { value: 'specific-days', label: 'Specific days only' },
  { value: 'event-timing', label: 'Event-specific timing' }
];

// Venue Type Options
const VENUE_TYPES = [
  { value: 'restaurant', label: 'Restaurant' },
  { value: 'bar-nightclub', label: 'Bar/nightclub' },
  { value: 'hotel-resort', label: 'Hotel/resort' },
  { value: 'retail', label: 'Retail' },
  { value: 'entertainment', label: 'Entertainment venue' },
  { value: 'sports-facility', label: 'Sports facility' },
  { value: 'other', label: 'Other' }
];

// Content Services Options
const CONTENT_SERVICES = [
  { value: 'graphic-design', label: 'Graphic design' },
  { value: 'video-production', label: 'Video production' },
  { value: 'copywriting', label: 'Copywriting/messaging' },
  { value: 'photography', label: 'Photography' },
  { value: 'animation', label: 'Animation/motion graphics' },
  { value: 'none', label: 'None - using existing assets only' }
];

// Content Creation Budget Options
const CONTENT_BUDGETS = [
  { value: 'under-500', label: 'Under $500' },
  { value: '500-1000', label: '$500-$1,000' },
  { value: '1000-2500', label: '$1,000-$2,500' },
  { value: '2500-5000', label: '$2,500-$5,000' },
  { value: 'over-5000', label: 'Over $5,000' },
  { value: 'need-quote', label: 'Need pricing quote' }
];

// US States
const US_STATES = [
  { value: 'AL', label: 'Alabama' }, { value: 'AK', label: 'Alaska' }, { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' }, { value: 'CA', label: 'California' }, { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' }, { value: 'DE', label: 'Delaware' }, { value: 'DC', label: 'District of Columbia' },
  { value: 'FL', label: 'Florida' }, { value: 'GA', label: 'Georgia' }, { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' }, { value: 'IL', label: 'Illinois' }, { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' }, { value: 'KS', label: 'Kansas' }, { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' }, { value: 'ME', label: 'Maine' }, { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' }, { value: 'MI', label: 'Michigan' }, { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' }, { value: 'MO', label: 'Missouri' }, { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' }, { value: 'NV', label: 'Nevada' }, { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' }, { value: 'NM', label: 'New Mexico' }, { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' }, { value: 'ND', label: 'North Dakota' }, { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' }, { value: 'OR', label: 'Oregon' }, { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' }, { value: 'SC', label: 'South Carolina' }, { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' }, { value: 'TX', label: 'Texas' }, { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' }, { value: 'VA', label: 'Virginia' }, { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' }, { value: 'WI', label: 'Wisconsin' }, { value: 'WY', label: 'Wyoming' }
];

const OrderForm = () => {
  const { user } = useAuth();
  const [availablePmps, setAvailablePmps] = useState([]);
  const [availableDisplayTargets, setAvailableDisplayTargets] = useState([]);
  const [availableContentSubcategories, setAvailableContentSubcategories] = useState([]);
  const [serverUser, setServerUser] = useState(null);
  const [isVenueUser, setIsVenueUser] = useState(false);
  
  // Set isVenueUser based on logged-in user's profile
  useEffect(() => {
    if (user) {
      const isVenue = user.isVenue === true || user.userType === 'venue';
      setIsVenueUser(isVenue);
      if (isVenue) {
        setServerUser(user);
      }
    }
  }, [user]);

  // Prefill form fields from authenticated user profile (do not overwrite user input)
  useEffect(() => {
    if (!user) return;

    const splitName = (full) => {
      const s = (full || '').trim().replace(/\s+/g, ' ');
      if (!s) return { first: '', last: '' };
      const parts = s.split(' ');
      if (parts.length === 1) return { first: parts[0], last: '' };
      return { first: parts[0], last: parts.slice(1).join(' ') };
    };

    const preferredNameSource =
      (user.contactPerson && user.userType === 'venue') ? user.contactPerson : (user.name || '');
    const { first, last } = splitName(preferredNameSource);

    setFormData(prev => {
      const next = { ...prev };

      // Contact fields
      if (!next.firstName && first) next.firstName = first;
      if (!next.lastName && last) next.lastName = last;
      if (!next.email && user.mail) next.email = user.mail;

      // Phone (best-effort; only if we have a phone on the user profile)
      const existingPhone = (next.phones?.[0]?.number || '').trim();
      const profilePhone = (user.contactPhone || user.phone || '').toString().trim();
      if (!existingPhone && profilePhone) {
        next.phones = Array.isArray(next.phones) && next.phones.length > 0
          ? [{ ...next.phones[0], number: profilePhone }, ...next.phones.slice(1)]
          : [{ type: 'office', number: profilePhone }];
      }

      // Company defaults (best-effort)
      if (!next.companyName && (user.companyName || user.venueName)) {
        next.companyName = user.companyName || user.venueName;
      }
      if (!next.companyIabCategory && user.companyIabCategory) {
        next.companyIabCategory = user.companyIabCategory;
      }

      return next;
    });
  }, [user]);
  const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phones: [{ type: 'office', number: '' }],
        companyName: '',
        companyIabCategory: '',
        agencyName: '',
        isAgency: false,
        address: '',
        city: '',
        state: '',
        zipCode: '',
        contentCategory: '',
        contentSubcategory: '',
        adProductCategory: '',
        selectedPmpTarget: '',
        selectedPmps: [],
        selectedDisplayUnitIds: [],
        campaignFormat: '',
        displayTarget: '',
        /** Human-readable name for support ticket & downstream trafficking (e.g. "Happy Hour"). */
        campaignDisplayName: '',
        /** Optional creative / asset targeting (shown in Campaign Details; applies when an asset is uploaded). */
        assetTargetGender: '',
        assetAgeBlock: '',
        assetTimeOfDay: '',
        campaignTerm: '',
        requestedStartDate: '',
        calculatedEndDate: '',
        specialRequests: '',
        campaignType: '',
        campaignObjective: '',
        whenShouldRun: '',
        timingDetails: '',
        daysOfWeek: [],
        startTime: '',
        endTime: '',
        venueType: '',
        typicalAudience: '',
        keyMessage: '',
        needContentCreated: false,
        contentServices: [],
        brandGuidelinesAvailable: '',
        contentCreationBudget: '',
        files: [], // Changed from single file to array
        termsAccepted: false
      });
  const [contentType, setContentType] = useState('');
  const [contentDescription, setContentDescription] = useState('');
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [missingFields, setMissingFields] = useState([]);
  const [showMissingFieldsModal, setShowMissingFieldsModal] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [fileMetadata, setFileMetadata] = useState({}); // Store metadata for each file
  const [resolutionWarning, setResolutionWarning] = useState(null); // Non-blocking video resolution conformance warning
  /** Venue UID(s) this campaign applies to (inventory PubIDs resolved server-side). Multi-venue accounts only. */
  const [orderVenueUids, setOrderVenueUids] = useState([]);
  const [venueLabelByUid, setVenueLabelByUid] = useState({});

  // Friendly labels for multi-venue checkboxes (same source as venue switcher)
  useEffect(() => {
    if (!isVenueUser || !user?.venues || user.venues.length <= 1) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch('/api/venues/my-venues', { credentials: 'include' });
        if (!r.ok || cancelled) return;
        const venues = await r.json();
        const map = {};
        venues.forEach((v) => {
          if (v.uid) map[v.uid] = v.label || `${v.publisherName || ''} — ${v.venueName || v.uid}`.trim();
        });
        if (!cancelled) setVenueLabelByUid(map);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isVenueUser, user?.venues]);

  // Default / sync selected venue(s) for PubID resolution when the account has venue scope
  useEffect(() => {
    if (!user) return;
    const uid = user.venueUid || serverUser?.venueUid;
    if (!uid) return;
    const venueList = (user.venues || []).map((v) => v.venue_uid).filter(Boolean);
    if (venueList.length <= 1) {
      setOrderVenueUids([uid]);
      return;
    }
    setOrderVenueUids((prev) => {
      const allowed = new Set(venueList);
      let next = prev.filter((x) => allowed.has(x));
      if (!next.includes(uid)) next = [...next, uid];
      if (next.length === 0) next = [uid];
      return next;
    });
  }, [user?.venueUid, user?.venues, serverUser?.venueUid]);

  const toggleOrderVenueUid = (uid) => {
    const active = user?.venueUid || serverUser?.venueUid;
    setOrderVenueUids((prev) => {
      const has = prev.includes(uid);
      if (has && prev.length <= 1) return prev;
      if (has) return prev.filter((x) => x !== uid);
      const next = [...prev, uid];
      if (active && !next.includes(active)) return [...next, active];
      return next;
    });
  };

  // CNTXT-47: gate the image-creative UI behind a build-time flag so it stays in lockstep
  // with the API flag (ALLOW_IMAGE_CREATIVES in server.js). The media player renders
  // images correctly now (CNTXT-51 resolved), so as of 2026-09-14 this DEFAULTS ON.
  // Set VITE_ALLOW_IMAGE_CREATIVES=false to turn images off again.
  //
  // NOTE: Vite inlines import.meta.env at BUILD time, so changing this variable requires
  // a rebuild, not just a restart — unlike the server-side flag, which only needs one.
  // Keep this in step with the server flag; a mismatch shows the user a bare 400.
  const ALLOW_IMAGE_CREATIVES = !/^(0|false|no|off)$/i.test(import.meta.env.VITE_ALLOW_IMAGE_CREATIVES || '');

  // Utility: Get allowed file types based on campaign format (CNTXT-47).
  // The selected campaign format decides the asset type: 'video' takes MP4, 'image'/
  // 'iab-standard' take images, 'mobile' takes either. Image types are additionally gated
  // by ALLOW_IMAGE_CREATIVES — if that switch is explicitly turned off, any image-capable
  // format falls back to MP4 with a note.
  const getAllowedFileTypes = () => {
    const MP4 = { accept: '.mp4', types: ['video/mp4'], label: 'MP4 video' };
    const IMG = {
      accept: '.jpg,.jpeg,.png,.gif,.webp',
      types: ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'],
      label: 'JPEG/PNG/GIF/WebP image'
    };
    const imagesDisabledNote = ' (image creatives are switched off)';

    if (formData.campaignFormat === 'video') {
      return { accept: MP4.accept, types: MP4.types, description: `${MP4.label} files` };
    }
    if (formData.campaignFormat === 'image' || formData.campaignFormat === 'iab-standard') {
      if (ALLOW_IMAGE_CREATIVES) {
        return { accept: IMG.accept, types: IMG.types, description: `${IMG.label} files` };
      }
      return { accept: MP4.accept, types: MP4.types, description: `${MP4.label} files${imagesDisabledNote}` };
    }
    if (formData.campaignFormat === 'mobile') {
      if (ALLOW_IMAGE_CREATIVES) {
        return {
          accept: `${MP4.accept},${IMG.accept}`,
          types: [...MP4.types, ...IMG.types],
          description: `${MP4.label} or ${IMG.label} files`
        };
      }
      return { accept: MP4.accept, types: MP4.types, description: `${MP4.label} files${imagesDisabledNote}` };
    }
    return { accept: '', types: [], description: '' };
  };

  // Utility functions must be declared here, outside of JSX and before return
  const formatPhoneNumber = (value) => {
    const phoneNumber = value.replace(/\D/g, '');
    if (phoneNumber.length === 0) return '';
    if (phoneNumber.length <= 3) return `(${phoneNumber}`;
    if (phoneNumber.length <= 6) return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  };

  const formatZipCode = (value) => {
    const zipCode = value.replace(/\D/g, '');
    if (zipCode.length <= 5) return zipCode;
    return `${zipCode.slice(0, 5)}-${zipCode.slice(5, 9)}`;
  };

  const hasValidPhone = () => {
    const digits = formData.phones
      .map(p => (p.number || '').replace(/\D/g, ''))
      .filter(n => n.length === 10);
    return digits.length > 0;
  };

  // Update available subcategories when content category changes
  useEffect(() => {
    if (formData.contentCategory) {
      const subcategories = getSubcategoriesForCategory(formData.contentCategory);
      setAvailableContentSubcategories(subcategories);
      setFormData(prev => ({ 
        ...prev, 
        contentSubcategory: '', 
        adProductCategory: '' 
      }));
    } else {
      setAvailableContentSubcategories([]);
      setFormData(prev => ({ ...prev, contentSubcategory: '', adProductCategory: '' }));
    }
  }, [formData.contentCategory]);

  // Update ad product when subcategory changes
  useEffect(() => {
    if (formData.contentCategory) {
      const adProduct = getAdProductForSelection(formData.contentCategory, formData.contentSubcategory);
      if (adProduct) {
        setFormData(prev => ({ ...prev, adProductCategory: adProduct.id }));
      }
    }
  }, [formData.contentCategory, formData.contentSubcategory]);

  // Update available display targets when campaign format changes
  useEffect(() => {
    if (formData.campaignFormat) {
      setAvailableDisplayTargets(DISPLAY_TARGETS[formData.campaignFormat] || []);
    } else {
      setAvailableDisplayTargets([]);
      setFormData(prev => ({ ...prev, displayTarget: '' }));
    }
  }, [formData.campaignFormat]);

  // If the logged-in user is already linked to a venue, prefill and restrict PMP on load
  useEffect(() => {
    const initForVenue = async (uid) => {
      try {
        const v = await fetch(`/api/venues/by-uid/${encodeURIComponent(uid)}`, { credentials: 'include' });
        if (v.ok) {
          const vd = await v.json();
          setFormData(prev => ({
            ...prev,
            address: vd.address || prev.address,
            city: vd.city || prev.city,
            state: vd.state || prev.state,
            zipCode: vd.zip || prev.zipCode,
            companyName: vd.name || prev.companyName,
          }));
        }
      } catch (e) {
        // ignore
      }
      try {
        const r = await fetch(`/api/venues/${encodeURIComponent(uid)}/pmp-options`, { credentials: 'include' });
        if (r.ok) {
          const opts = await r.json();
          setAvailablePmps(opts.pmps);
          setFormData(prev => ({
            ...prev,
            selectedPmpTarget: opts.target,
            selectedPmps: opts.pmps.map(p => p.value),
          }));
        } else {
          setAvailablePmps(PMP_TARGETS.routeOperations.pmps);
        }
      } catch (e) {
        setAvailablePmps(PMP_TARGETS.routeOperations.pmps);
      }
    };
    if (isVenueUser && serverUser?.venueUid) {
      initForVenue(serverUser.venueUid);
    } else {
      setAvailablePmps(PMP_TARGETS.routeOperations.pmps);
    }
  }, [isVenueUser, serverUser?.venueUid]);

  // Calculate end date when start date or campaign term changes
  useEffect(() => {
    // Brands/agencies: auto-calc end date from campaign term
    if (!isVenueUser && formData.requestedStartDate && formData.campaignTerm) {
      const term = CAMPAIGN_TERMS.find(t => t.value === formData.campaignTerm);
      if (term) {
        const startDate = new Date(formData.requestedStartDate);
        const endDate = new Date(startDate);
        endDate.setMonth(endDate.getMonth() + term.months);
        const formattedEndDate = (endDate.getMonth() + 1).toString().padStart(2, '0') + 
                                 '/' + endDate.getDate().toString().padStart(2, '0') + 
                                 '/' + endDate.getFullYear();
        setFormData(prev => ({
          ...prev,
          calculatedEndDate: formattedEndDate
        }));
      }
    } else if (isVenueUser) {
      // Venues: end date is selected explicitly, not calculated
      // Do not override venue-selected end date here.
    }
  }, [formData.requestedStartDate, formData.campaignTerm, isVenueUser]);


  useEffect(() => {
    if (formData.contentCategory) {
      const subcategories = getSubcategoriesForCategory(formData.contentCategory);
      setAvailableContentSubcategories(subcategories);
      setFormData(prev => ({ 
        ...prev, 
        contentSubcategory: '', 
        adProductCategory: '' 
      }));
    } else {
      // ...existing code...
    }
  }, [formData.contentCategory]);

  useEffect(() => {
    if (formData.contentCategory) {
      const subcategories = getSubcategoriesForCategory(formData.contentCategory);
      setAvailableContentSubcategories(subcategories);
      setFormData(prev => ({ 
        ...prev, 
        contentSubcategory: '', 
        adProductCategory: '' 
      }));
    } else {
      setAvailableContentSubcategories([]);
      setFormData(prev => ({ ...prev, contentSubcategory: '', adProductCategory: '' }));
    }
  }, [formData.contentCategory]);

  // Update ad product when subcategory changes
  useEffect(() => {
    if (formData.contentCategory) {
      const adProduct = getAdProductForSelection(formData.contentCategory, formData.contentSubcategory);
      if (adProduct) {
        setFormData(prev => ({ ...prev, adProductCategory: adProduct.id }));
      }
    }
  }, [formData.contentCategory, formData.contentSubcategory]);

  // Update available display targets when campaign format changes
  useEffect(() => {
    if (formData.campaignFormat) {
      setAvailableDisplayTargets(DISPLAY_TARGETS[formData.campaignFormat] || []);
    } else {
      setAvailableDisplayTargets([]);
      setFormData(prev => ({ ...prev, displayTarget: '' }));
    }
  }, [formData.campaignFormat]);

  // If the logged-in user is already linked to a venue, prefill and restrict PMP on load
  useEffect(() => {
    const initForVenue = async (uid) => {
      try {
        const v = await fetch(`/api/venues/by-uid/${encodeURIComponent(uid)}`, { credentials: 'include' });
        if (v.ok) {
          const vd = await v.json();
          setFormData(prev => ({
            ...prev,
            address: vd.address || prev.address,
            city: vd.city || prev.city,
            state: vd.state || prev.state,
            zipCode: vd.zip || prev.zipCode,
            companyName: vd.name || prev.companyName,
          }));
        }
      } catch (e) {
        // ignore
      }
      try {
        const r = await fetch(`/api/venues/${encodeURIComponent(uid)}/pmp-options`, { credentials: 'include' });
        if (r.ok) {
          const opts = await r.json();
          setAvailablePmps(opts.pmps);
          setFormData(prev => ({
            ...prev,
            selectedPmpTarget: opts.target,
            selectedPmps: opts.pmps.map(p => p.value),
          }));
        } else {
          setAvailablePmps(PMP_TARGETS.routeOperations.pmps);
        }
      } catch (e) {
        setAvailablePmps(PMP_TARGETS.routeOperations.pmps);
      }
    };
    if (isVenueUser && serverUser?.venueUid) {
      initForVenue(serverUser.venueUid);
    } else {
      setAvailablePmps(PMP_TARGETS.routeOperations.pmps);
    }
  }, [isVenueUser, serverUser?.venueUid]);

  // Calculate end date when start date or campaign term changes
  useEffect(() => {
    // Brands/agencies: auto-calc end date from campaign term
    if (!isVenueUser && formData.requestedStartDate && formData.campaignTerm) {
      const term = CAMPAIGN_TERMS.find(t => t.value === formData.campaignTerm);
      if (term) {
        const startDate = new Date(formData.requestedStartDate);
        const endDate = new Date(startDate);
        endDate.setMonth(endDate.getMonth() + term.months);
        
        const formattedEndDate = (endDate.getMonth() + 1).toString().padStart(2, '0') + 
                                 '/' + endDate.getDate().toString().padStart(2, '0') + 
                                 '/' + endDate.getFullYear();
        
        setFormData(prev => ({
          ...prev,
          calculatedEndDate: formattedEndDate
        }));
      }
    } else if (isVenueUser) {
      // Venues: end date is selected explicitly, not calculated
      // Do not override venue-selected end date here.
    }
  }, [formData.requestedStartDate, formData.campaignTerm, isVenueUser]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePhoneChange = (index, field, value) => {
    const newPhones = [...formData.phones];
    newPhones[index] = { ...newPhones[index], [field]: value };
    setFormData(prev => ({ ...prev, phones: newPhones }));
  };

  const addPhone = () => {
    setFormData(prev => ({
      ...prev,
      phones: [...prev.phones, { type: 'mobile', number: '' }]
    }));
  };

  const removePhone = (index) => {
    if (formData.phones.length > 1) {
      const newPhones = formData.phones.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, phones: newPhones }));
    }
  };

  const handlePmpTargetChange = (target) => {
    setFormData(prev => ({ 
      ...prev, 
      selectedPmpTarget: target,
      selectedPmps: []
    }));
  };

  const handlePmpSelection = (pmpValue) => {
    const isSelected = formData.selectedPmps.includes(pmpValue);
    if (isSelected) {
      setFormData(prev => ({
        ...prev,
        selectedPmps: prev.selectedPmps.filter(p => p !== pmpValue)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        selectedPmps: [...prev.selectedPmps, pmpValue]
      }));
    }
  };

  // File validation function
  const validateFile = (file) => {
    const maxSize = 50 * 1024 * 1024; // 50MB
    const allowedTypes = getAllowedFileTypes();
    
    if (file.size > maxSize) {
  return `File ${file.name} is too large. Maximum size is 50MB.`;
    }
    
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    const isValidExtension = allowedTypes.accept.split(',').some(ext => ext.trim() === fileExtension);
    const isValidMimeType = allowedTypes.types.includes(file.type);
    
    if (!isValidExtension && !isValidMimeType) {
      return `File ${file.name} has an unsupported format. Please upload ${allowedTypes.description} only.`;
    }
    
    return null;
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  // Approved creative resolutions for video (exact width x height match).
  // Keep in sync with the Creative Specifications table / spec sheet / server check.
  const ALLOWED_VIDEO_RESOLUTIONS = [
    [1920, 1080], [1280, 720], [1080, 1920], [1024, 768], [640, 240], [320, 320],
  ];

  // Fallback path: read the file's raw bytes and parse the MP4 box structure
  // directly. Used when the <video> element can't (or won't, in time) report
  // dimensions -- notably for MP4s exported without "faststart", where the
  // moov atom sits after mdat and some browsers never fire loadedmetadata.
  const probeVideoResolutionFromBytes = async (file) => {
    try {
      const buffer = await file.arrayBuffer();
      return parseIsoBmffDimensions(buffer);
    } catch (_) {
      return null;
    }
  };

  // Read a video file's pixel dimensions in the browser (no upload required).
  const probeVideoResolution = (file) => new Promise((resolve) => {
    let settled = false;
    const finish = (result) => {
      if (settled) return;
      settled = true;
      resolve(result);
    };
    try {
      const url = URL.createObjectURL(file);
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        const width = video.videoWidth;
        const height = video.videoHeight;
        URL.revokeObjectURL(url);
        if (width && height) {
          finish({ width, height });
        } else {
          probeVideoResolutionFromBytes(file).then(finish);
        }
      };
      video.onerror = () => {
        URL.revokeObjectURL(url);
        probeVideoResolutionFromBytes(file).then(finish);
      };
      video.src = url;
      // Some browsers never fire loadedmetadata/error for MP4s with a trailing
      // moov atom (not "faststart"). Don't hang forever -- fall back to a
      // direct byte-level read, which works regardless of atom order.
      setTimeout(() => {
        if (!settled) probeVideoResolutionFromBytes(file).then(finish);
      }, 2500);
    } catch (_) {
      probeVideoResolutionFromBytes(file).then(finish);
    }
  });

  // Check the selected video and set a non-blocking warning if it isn't an approved
  // resolution. Submission is still allowed; the warning is also recorded on the ticket.
  const checkVideoResolution = async (file) => {
    if (!file || !(file.type || '').toLowerCase().startsWith('video/')) {
      setResolutionWarning(null);
      return;
    }
    const dims = await probeVideoResolution(file);
    if (!dims || !dims.width || !dims.height) {
      setResolutionWarning("We couldn't automatically read this video's resolution. Please double-check it matches an approved size before submitting.");
      return;
    }
    const conforms = ALLOWED_VIDEO_RESOLUTIONS.some(([w, h]) => w === dims.width && h === dims.height);
    if (conforms) {
      setResolutionWarning(null);
    } else {
      setResolutionWarning(
        `Heads up: this video is ${dims.width}×${dims.height}, which is not one of the approved creative resolutions ` +
        `(1920×1080, 1280×720, 1080×1920, 1024×768, 640×240, 320×320). ` +
        `You can still submit, but the display network may reject it. Our team will also be notified to review.`
      );
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0) {
      const validFiles = [];
      for (const file of droppedFiles) {
        const error = validateFile(file);
        if (error) {
          alert(error);
          continue;
        }
        validFiles.push(file);
      }

      if (validFiles.length > 0) {
        setFormData(prev => ({
          ...prev,
          // Single-file only: replace any existing selection
          files: [validFiles[0]]
        }));
        checkVideoResolution(validFiles[0]);
      }
    }
  };

  // File input change handler (single file)
  const handleFileUpload = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (selectedFiles.length === 0) return;

    const validFiles = [];
    for (const file of selectedFiles) {
      const error = validateFile(file);
      if (error) {
        alert(error);
        continue;
      }
      validFiles.push(file);
    }

    if (validFiles.length > 0) {
      setFormData(prev => ({
        ...prev,
        // Single-file only: replace any existing selection
        files: [validFiles[0]]
      }));
      checkVideoResolution(validFiles[0]);
    }

    // Reset input to allow selecting the same file again
    event.target.value = '';
  };

  const removeFile = (index) => {
    setFormData(prev => {
      const newFiles = prev.files.filter((_, i) => i !== index);
      return {
        ...prev,
        files: newFiles
      };
    });
    // Remove metadata for this file
    setFileMetadata(prev => {
      const newMetadata = { ...prev };
      // Remove by index (since files array changed, we need to track by file name)
      const fileToRemove = formData.files[index];
      if (fileToRemove) {
        delete newMetadata[fileToRemove.name];
      }
      return newMetadata;
    });
    // Clear any resolution warning tied to the removed file
    setResolutionWarning(null);
  };

  // T&C Modal scroll handler
  const handleTermsScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const isScrolledToBottom = Math.abs(scrollHeight - clientHeight - scrollTop) < 5;
    setHasScrolledToBottom(isScrolledToBottom);
  };

  // Creative Specifications block (visible to all users before upload)
  const CreativeSpecs = () => (
    <div className="mb-6">
      <div className="flex items-start justify-between gap-3 mb-2">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Creative Specifications (Programmatic DOOH)</h3>
        <a
          href="/api/creative-specs.pdf"
          download="CPMN-Creative-Specifications.pdf"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg border border-blue-600 text-blue-700 hover:bg-blue-50 dark:text-blue-300 dark:border-blue-400 dark:hover:bg-blue-900/20 whitespace-nowrap flex-shrink-0"
        >
          <Download className="w-4 h-4" />
          Download Spec Sheet (PDF)
        </a>
      </div>
      <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">Please follow these guidelines when preparing ad creative. If you need help, check “Need Content Created” and our team will assist. Download the spec sheet to share with your creative team.</p>

      {/* Desktop table */}
      <div className="hidden sm:block overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 mb-4">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800/60">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider w-48">Category</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Parameter</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Requirement</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700 text-sm">
            {/* Video rows */}
            <tr>
              <td rowSpan={6} className="px-4 py-3 align-top font-medium text-gray-900 dark:text-gray-100">Video</td>
              <td className="px-4 py-3">Duration</td>
              <td className="px-4 py-3">15 or 30 seconds</td>
            </tr>
            <tr>
              <td className="px-4 py-3">Format / Codec</td>
              <td className="px-4 py-3">MP4 (H.264/AVC)</td>
            </tr>
            <tr>
              <td className="px-4 py-3 align-top">Resolutions &amp; Aspect Ratios</td>
              <td className="px-4 py-3">
                <table className="min-w-full border border-gray-200 dark:border-gray-700 text-xs">
                  <thead className="bg-gray-50 dark:bg-gray-800/60">
                    <tr>
                      <th className="px-2 py-1 text-left font-medium">Resolution</th>
                      <th className="px-2 py-1 text-left font-medium">Aspect Ratio</th>
                      <th className="px-2 py-1 text-left font-medium">Use Case</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    <tr><td className="px-2 py-1">1920x1080</td><td className="px-2 py-1">16:9</td><td className="px-2 py-1">Main displays (landscape)</td></tr>
                    <tr><td className="px-2 py-1">1280x720</td><td className="px-2 py-1">16:9</td><td className="px-2 py-1">Fallback/lower-bandwidth displays</td></tr>
                    <tr><td className="px-2 py-1">1080x1920</td><td className="px-2 py-1">9:16</td><td className="px-2 py-1">Kiosk/vertical displays</td></tr>
                    <tr><td className="px-2 py-1">1024x768</td><td className="px-2 py-1">4:3</td><td className="px-2 py-1">Legacy venue hardware</td></tr>
                    <tr><td className="px-2 py-1">640x240</td><td className="px-2 py-1">8:3</td><td className="px-2 py-1">Slot service windows</td></tr>
                    <tr><td className="px-2 py-1">320x320</td><td className="px-2 py-1">1:1</td><td className="px-2 py-1">Square/supplementary displays</td></tr>
                  </tbody>
                </table>
              </td>
            </tr>
            <tr>
              <td className="px-4 py-3">Frame Rate</td>
              <td className="px-4 py-3">24–30 fps</td>
            </tr>
            <tr>
              <td className="px-4 py-3">Audio</td>
              <td className="px-4 py-3">Optional</td>
            </tr>
            <tr>
              <td className="px-4 py-3">Bitrate & File Size</td>
              <td className="px-4 py-3">Target 10–20 Mbps; maximum 50 MB file size</td>
            </tr>

            {/* Image rows hidden: static image uploads are temporarily disabled (media player
                cannot render images yet). Re-enable this block when image support returns. */}

            {/* Shared guidance */}
            <tr>
              <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">Safe Zone</td>
              <td className="px-4 py-3" colSpan={2}>Keep key text and logos within the central 90% of the frame to avoid cropping on various displays.</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Mobile bullets */}
      <div className="sm:hidden space-y-3 text-sm text-gray-700 dark:text-gray-300">
        <div>
          <p className="font-semibold text-gray-900 dark:text-white">Video</p>
          <ul className="list-disc pl-5">
            <li>15 or 30 seconds</li>
            <li>MP4 (H.264/AVC)</li>
            <li>24–30 fps; audio optional</li>
            <li>Target 10–20 Mbps; max 50 MB</li>
          </ul>
          <p className="font-medium mt-2 text-gray-900 dark:text-white">Resolutions &amp; Aspect Ratios</p>
          <ul className="list-disc pl-5">
            <li>1920x1080 (16:9) — Main displays (landscape)</li>
            <li>1280x720 (16:9) — Fallback/lower-bandwidth displays</li>
            <li>1080x1920 (9:16) — Kiosk/vertical displays</li>
            <li>1024x768 (4:3) — Legacy venue hardware</li>
            <li>640x240 (8:3) — Slot service windows</li>
            <li>320x320 (1:1) — Square/supplementary displays</li>
          </ul>
        </div>
        {/* Static Image bullets hidden: image uploads are temporarily disabled until the
            media player supports images. Re-enable alongside the desktop table block. */}
        <p className="italic text-gray-600 dark:text-gray-400">Tip: Keep key text/logos within the central 90% of the frame.</p>
      </div>
    </div>
  );

  // Validation function that collects missing fields
  const validateForm = () => {
    const missing = [];
    
    if (!formData.firstName) missing.push('First Name');
    if (!formData.lastName) missing.push('Last Name');
    if (!formData.email) {
      missing.push('Email Address');
    } else {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(formData.email)) {
        missing.push('Valid Email Address');
      }
    }
    if (!hasValidPhone()) missing.push('At least one Phone Number');

    // Company and address details are required only for brands/agencies;
    // for venues we already know this from their account setup.
    // Note: companyName and companyIabCategory come from user profile, not form input
    if (!isVenueUser) {
      // companyName and companyIabCategory come from user profile, skip validation
      if (formData.isAgency && !formData.agencyName) missing.push('Agency Name');

      if (!formData.address) missing.push('Street Address');
      if (!formData.city) missing.push('City');
      if (!formData.state) missing.push('State');
      if (!formData.zipCode) missing.push('ZIP Code');
    }

    // Campaign taxonomy not required for venues; brands/agencies must provide it
    if (!isVenueUser) {
      if (!formData.contentCategory) missing.push('Content Category');
      if (!formData.adProductCategory) missing.push('Ad Product Category');
    }

    if (!formData.selectedDisplayUnitIds?.length) {
      missing.push('At least one live display unit');
    }
    
    // Investment Tier selection removed from user flow
    
    if (!formData.campaignFormat) missing.push('Campaign Format');
    if (!formData.displayTarget) missing.push('Display Target');
    if (isVenueUser && !(formData.campaignDisplayName || '').trim()) {
      missing.push('Campaign Name (for support ticket)');
    }
    
    // Venues advertise on their own displays for free, so no commitment term
    if (!isVenueUser) {
      if (!formData.campaignTerm) missing.push('Campaign Term');
    }
    if (!formData.requestedStartDate) missing.push('Start Date');
    
    // New campaign detail validations (still valuable for venues)
    if (!formData.campaignType) missing.push('Campaign Type');
    if (!formData.campaignObjective) missing.push('Campaign Objective');
    if (!formData.whenShouldRun) missing.push('When Should This Run');
    // When specific timing options are chosen, require a descriptive timingDetails note
    // Only require Timing Details for event-timing
    if (
      formData.whenShouldRun === 'event-timing' &&
      !formData.timingDetails
    ) {
      missing.push('Timing Details');
    }
    if (!formData.venueType && !isVenueUser) missing.push('Venue Type');
    if (!formData.typicalAudience && !isVenueUser) missing.push('Typical Audience');
    if (!formData.keyMessage && !isVenueUser) missing.push('Key Message/Offer');

    // Creative / assets rules
    // Venues: MUST complete Creative Assets section.
    // If "Need content created" is checked, no asset file is required.
    // Otherwise, at least one asset file must be provided.
    if (!formData.needContentCreated && (!formData.files || formData.files.length === 0)) {
      missing.push('Campaign Asset File');
    }
    
    // Content creation validations
    if (formData.needContentCreated) {
      if (!formData.contentServices.length) missing.push('Content Services');
      if (!formData.brandGuidelinesAvailable) missing.push('Brand Guidelines Availability');
      if (!formData.contentCreationBudget) missing.push('Content Creation Budget');
    }
    // When content creation is requested, content type/description are optional for now

    if (!formData.termsAccepted) missing.push('Terms and Conditions Acceptance');

    return missing;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const missing = validateForm();
    
    if (missing.length > 0) {
      setMissingFields(missing);
      setShowMissingFieldsModal(true);
      return;
    }

    try {
      const submitData = new FormData();

      submitData.append('advertiserName', formData.companyName);
      submitData.append('contactEmail', formData.email);
      submitData.append('contactPhone', formData.phones[0]?.number || '');
      const campaignTechnicalLabel = `${formData.campaignFormat} - ${formData.displayTarget}`;
      const displayTitle = (formData.campaignDisplayName || '').trim();
      submitData.append(
        'campaignName',
        displayTitle || campaignTechnicalLabel
      );
      submitData.append('adType', formData.campaignFormat);
      submitData.append('startDate', formData.requestedStartDate);
      submitData.append('endDate', formData.calculatedEndDate);
      submitData.append('targetAudience', formData.contentCategory);
      submitData.append('specialInstructions', formData.specialRequests || '');
      
      const venueUidsForPayload =
        isVenueUser
          ? (orderVenueUids.length > 0
              ? orderVenueUids
              : user?.venueUid
                ? [user.venueUid]
                : [])
          : [];

      const campaignTypeLabel =
        CAMPAIGN_TYPES.find((t) => t.value === formData.campaignType)?.label || '';
      const whenShouldRunLabel =
        CAMPAIGN_TIMING.find((t) => t.value === formData.whenShouldRun)?.label || '';
      const displayTargetLabel =
        availableDisplayTargets.find((t) => t.value === formData.displayTarget)?.label || '';

      submitData.append('formDataJson', JSON.stringify({
        firstName: formData.firstName,
        lastName: formData.lastName,
        companyIabCategory: formData.companyIabCategory,
        isAgency: formData.isAgency,
        agencyName: formData.agencyName,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        zipCode: formData.zipCode,
        phones: formData.phones,
        selectedPmpTarget: formData.selectedPmpTarget,
        selectedPmps: formData.selectedPmps,
        selectedDisplayUnitIds: formData.selectedDisplayUnitIds,
        campaignTerm: formData.campaignTerm,
        adProductCategory: formData.adProductCategory,
        displayTarget: formData.displayTarget,
        campaignDisplayName: (formData.campaignDisplayName || '').trim() || null,
        campaignTechnicalLabel,
        campaignTypeLabel,
        displayTargetLabel,
        whenShouldRunLabel,
        contentSubcategory: formData.contentSubcategory,
        // New campaign fields
        campaignType: formData.campaignType,
        campaignObjective: formData.campaignObjective,
        whenShouldRun: formData.whenShouldRun,
        timingDetails: formData.timingDetails,
        daysOfWeek: formData.daysOfWeek,
        startTime: formData.startTime,
        endTime: formData.endTime,
        specialRequests: formData.specialRequests || '',
        assetTargetGender: formData.assetTargetGender || '',
        assetAgeBlock: formData.assetAgeBlock || '',
        assetTimeOfDay: formData.assetTimeOfDay || '',
        venueType: formData.venueType,
        typicalAudience: formData.typicalAudience,
        keyMessage: formData.keyMessage,
        needContentCreated: formData.needContentCreated,
        contentServices: formData.contentServices,
        brandGuidelinesAvailable: formData.brandGuidelinesAvailable,
        contentCreationBudget: formData.contentCreationBudget,
        contentType,
        contentDescription,
        ...(venueUidsForPayload.length > 0 ? { orderVenueUids: venueUidsForPayload } : {}),
      }));

      // Append a single file (multi-file removed)
      if (!formData.needContentCreated && formData.files && formData.files.length > 0) {
        const file = formData.files[0];
        if (file) submitData.append('files', file);
        
        // Send file metadata (Campaign Details targeting + any legacy per-file keys)
        const metadataToSend = {};
        const merged = {
          ...(file ? fileMetadata[file.name] || {} : {}),
          gender: formData.assetTargetGender || '',
          ageBlock: formData.assetAgeBlock || '',
          timeOfDay: formData.assetTimeOfDay || '',
        };
        if (file) {
          metadataToSend[0] = merged;
          metadataToSend[file.name] = merged;
          submitData.append('fileMetadata', JSON.stringify(metadataToSend));
        }
      }

      console.log('📤 Submitting order with files:', formData.files ? `${formData.files.length} file(s)` : 'No files');

      const response = await fetch('/api/orders', {
        method: 'POST',
        body: submitData,
        credentials: 'include'
      });

      // Handle session expired or unauthorized
      if (response.status === 401) {
        alert('Your session has expired. Please log in again.');
        // Clear cookies/localStorage if needed
        window.location.href = '/login';
        return;
      }

      // Try to parse JSON, but catch HTML responses
      let result;
      try {
        result = await response.json();
      } catch (err) {
        // If response is not JSON (e.g., HTML), force logout
        alert('Session expired or server error. Please log in again.');
        window.location.href = '/login';
        return;
      }

      if (response.ok && result && result.success) {
        let successMessage = `Order submitted successfully!\nOrder ID: ${result.id}`;
        if (result.zammad_ticket) {
          successMessage += `\nSupport Ticket: ${result.zammad_ticket.number}`;
        }
        alert(successMessage);
        // Reset campaign-specific fields only; keep contact + venue context for next submission
        setFormData(prev => ({
          ...prev,
          // Campaign taxonomy
          contentCategory: '',
          contentSubcategory: '',
          adProductCategory: '',
          // Campaign selection
          campaignFormat: '',
          displayTarget: '',
          campaignDisplayName: '',
          assetTargetGender: '',
          assetAgeBlock: '',
          assetTimeOfDay: '',
          // Dates/notes
          campaignTerm: '',
          requestedStartDate: '',
          calculatedEndDate: '',
          specialRequests: '',
          // New campaign fields
          campaignType: '',
          campaignObjective: '',
          whenShouldRun: '',
          timingDetails: '',
          daysOfWeek: [],
          startTime: '',
          endTime: '',
          venueType: prev.venueType, // keep if user already chose it
          typicalAudience: prev.typicalAudience, // keep if user already chose it
          keyMessage: prev.keyMessage, // keep if user already chose it
          // Creative section
          needContentCreated: false,
          contentServices: [],
          brandGuidelinesAvailable: '',
          contentCreationBudget: '',
          files: [],
          termsAccepted: false,
        }));
        setFileMetadata({});
        setContentType('');
        setContentDescription('');
        const fileInput = document.getElementById('file-upload');
        if (fileInput) {
          fileInput.value = '';
        }
      } else {
        const detail = Array.isArray(result?.detail)
          ? ` — ${result.detail.join('; ')}`
          : (result?.detail ? ` — ${result.detail}` : '');
        throw new Error(`${result?.error || 'Failed to submit order'}${detail}`);
      }
    } catch (error) {
      console.error('Error submitting order:', error);
      alert(`Error submitting order: ${error.message}. Please try again.`);
    }
  };

  // Venue linking removed: assignment is handled by admins

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 transition-colors duration-300">
      {/* Account type banner */}
      <div className="max-w-6xl mx-auto px-6 pt-4">
        <div className={`mb-4 p-3 rounded-lg ${isVenueUser ? 'bg-green-50 border border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-300' : 'bg-blue-50 border border-blue-200 text-blue-800 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-300'}`}>
          {isVenueUser ? 'Venue account detected. You can upload existing creative or request content creation below.' : 'Logged in. Complete the form, attach media or request content creation, accept the terms, and submit.'}
        </div>
        {isVenueUser && user?.venues && user.venues.length > 1 && (
          <div className="mb-4 p-4 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 text-amber-950 dark:text-amber-100">
            <p className="text-sm font-medium mb-2">Campaign applies to these venue(s)</p>
            <p className="text-xs opacity-90 mb-3">PubIDs for each selected venue are included on your support ticket for trafficking (Advangelists).</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-4 gap-y-2 max-h-[min(70vh,560px)] overflow-y-auto overscroll-contain pr-1">
              {user.venues.map((v) => (
                <label key={v.venue_uid} className="flex items-start gap-2 cursor-pointer text-sm leading-snug">
                  <input
                    type="checkbox"
                    checked={orderVenueUids.includes(v.venue_uid)}
                    onChange={() => toggleOrderVenueUid(v.venue_uid)}
                    className="mt-0.5 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="break-words">{venueLabelByUid[v.venue_uid] || v.venue_uid}</span>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>
      {/* Header with Context Networks Corporate Logo and Form Info */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm transition-colors duration-300">
        <div className="max-w-6xl mx-auto px-6 py-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-8">
            {/* Logo */}
            <div className="flex-shrink-0 flex justify-center md:justify-start">
              <div className="w-40 h-40 md:w-60 md:h-60 bg-white dark:bg-gray-800 rounded-xl flex items-center justify-center shadow-lg border border-gray-200 dark:border-gray-600 p-4 md:p-6">
                <img 
                  src="/images/context-networks-logo.png" 
                  alt="Context Networks"
                  className="w-32 h-32 md:w-52 md:h-52 object-contain"
                />
              </div>
            </div>
            {/* Form header */}
            <div className="flex flex-col flex-1 gap-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                  Order Insertion Form
                </h1>
                <p className="text-lg text-gray-600 dark:text-gray-400">
                  Complete the form below to submit your advertising campaign request
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden transition-colors duration-300">
          
          {/* Contact Information */}
          <div className="px-8 py-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-2 mb-6">
              <Building className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Contact Information</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 transition-colors duration-200"
                  placeholder="Your first name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 transition-colors duration-200"
                  placeholder="Your last name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 transition-colors duration-200"
                  placeholder="your.email@company.com"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Order Insertion details will be sent to this address.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <Phone className="w-4 h-4 inline mr-1" />
                  Phone Numbers <span className="text-red-500">*</span>
                </label>
                {formData.phones.map((phone, index) => (
                  <div key={index} className="grid grid-cols-4 gap-2 mb-2">
                    <select
                      value={phone.type}
                      onChange={(e) => handlePhoneChange(index, 'type', e.target.value)}
                      className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="office">Office</option>
                      <option value="mobile">Mobile</option>
                    </select>
                    <div className="col-span-3 flex">
                      <input
                        type="tel"
                        value={phone.number}
                        onChange={(e) => handlePhoneChange(index, 'number', e.target.value)}
                        onBlur={(e) => {
                          const formatted = formatPhoneNumber(e.target.value);
                          handlePhoneChange(index, 'number', formatted);
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-l-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                        placeholder="(555) 123-4567"
                        required={index === 0}
                      />
                      {formData.phones.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removePhone(index)}
                          className="px-3 py-2 bg-red-500 hover:bg-red-600 text-white rounded-r-lg text-sm transition-colors duration-200"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addPhone}
                  className="mt-2 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 text-sm font-medium"
                >
                  + Add Another Phone
                </button>
              </div>
            </div>
          </div>

          {/* Company Address (brands/agencies only; venues already have this on file) */}
          {!isVenueUser && (
          <div className="px-8 py-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-2 mb-6">
              <MapPin className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Company Address</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Street Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 transition-colors duration-200"
                  placeholder="123 Main St"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  City <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 transition-colors duration-200"
                  placeholder="City"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    State <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.state}
                    onChange={(e) => handleInputChange('state', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    required
                  >
                    <option value="">Select</option>
                    {US_STATES.map(s => (
                      <option key={s.value} value={s.value}>{s.value}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    ZIP Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.zipCode}
                    onChange={(e) => handleInputChange('zipCode', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 transition-colors duration-200"
                    placeholder="12345"
                  />
                </div>
              </div>
            </div>
          </div>
          )}

          {/* Campaign Categories (hidden for venues) */}
          {!isVenueUser && (
          <div className="px-8 py-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-2 mb-6">
              <Tag className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Campaign Categories</h2>
              <span className="text-sm text-gray-500 dark:text-gray-400">(Official IAB Taxonomy)</span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Content Category <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.contentCategory}
                  onChange={(e) => handleInputChange('contentCategory', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                >
                  <option value="">Select Content Category</option>
                  {COMPLETE_TAXONOMY_DATA.contentCategories.map(category => (
                    <option key={category.value} value={category.value}>
                      {category.value}: {category.label}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Content Subcategory
                </label>
                <select
                  value={formData.contentSubcategory}
                  onChange={(e) => handleInputChange('contentSubcategory', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  disabled={availableContentSubcategories.length === 0}
                >
                  <option value="">Select Subcategory</option>
                  {availableContentSubcategories.map(subcategory => (
                    <option key={subcategory.value} value={subcategory.value}>
                      {subcategory.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {availableContentSubcategories.length === 0 
                    ? 'Select content category first'
                    : `${availableContentSubcategories.length} subcategories available`
                  }
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Ad Product Category <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.adProductCategory ? `${formData.adProductCategory}: ${getAdProductForSelection(formData.contentCategory, formData.contentSubcategory)?.name || ''}` : ''}
                  readOnly
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-600 text-gray-700 dark:text-gray-300"
                  placeholder="Auto-selected based on category selection"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {formData.contentCategory
                    ? 'Automatically determined by your content category selection'
                    : 'Select content category first to see ad product category'
                  }
                </p>
              </div>
            </div>
          </div>
          )}

          <InventoryPicker
            selectedIds={formData.selectedDisplayUnitIds || []}
            onChange={(ids) => setFormData((prev) => ({ ...prev, selectedDisplayUnitIds: ids }))}
            venueUid={isVenueUser ? (user?.venueUid || '') : ''}
          />
          {/* Legacy PMP block kept hidden — live Control inventory is the picker now */}
          {false && !isVenueUser && (
          <div className="px-8 py-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-2 mb-6">
              <Target className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Choose Target PMP</h2>
              <span className="text-sm text-gray-500 dark:text-gray-400">(Select One)</span>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Casino - Disabled */}
                <div className="p-4 border-2 rounded-lg cursor-not-allowed opacity-50 bg-gray-100 dark:bg-gray-700/50 border-gray-300 dark:border-gray-600">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-gray-400 dark:text-gray-500">Casino</h4>
                      <p className="text-sm text-gray-400 dark:text-gray-500">Coming Soon</p>
                    </div>
                    <input
                      type="radio"
                      disabled
                      className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 opacity-50"
                    />
                  </div>
                </div>

                {/* Route Operations - Enabled */}
                <div
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-colors duration-200 ${
                    formData.selectedPmpTarget === 'routeOperations'
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-300 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-400'
                  }`}
                  onClick={() => handlePmpTargetChange('routeOperations')}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white">Route Operations</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Available Now</p>
                    </div>
                    <input
                      type="radio"
                      checked={formData.selectedPmpTarget === 'routeOperations'}
                      onChange={() => handlePmpTargetChange('routeOperations')}
                      className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                    />
                  </div>
                </div>

                {/* Online/Mobile Apps - Disabled */}
                <div className="p-4 border-2 rounded-lg cursor-not-allowed opacity-50 bg-gray-100 dark:bg-gray-700/50 border-gray-300 dark:border-gray-600">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-gray-400 dark:text-gray-500">Online/Mobile Apps</h4>
                      <p className="text-sm text-gray-400 dark:text-gray-500">Coming Soon</p>
                    </div>
                    <input
                      type="radio"
                      disabled
                      className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 opacity-50"
                    />
                  </div>
                </div>
              </div>

              {/* PMP Selection */}
              {formData.selectedPmpTarget === 'routeOperations' && (
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Select Route Operations PMPs <span className="text-red-500">*</span>
                  </label>
                  <div className="space-y-2">
                    {availablePmps.map(pmp => (
                      <div
                        key={pmp.value}
                        className={`p-3 border rounded-lg cursor-pointer transition-colors duration-200 ${
                          formData.selectedPmps.includes(pmp.value)
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                            : 'border-gray-300 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-400'
                        }`}
                        onClick={() => handlePmpSelection(pmp.value)}
                      >
                        <div className="flex items-center justify-between">
                          <label className="text-sm font-medium text-gray-900 dark:text-white cursor-pointer">
                            {pmp.label}
                          </label>
                          <input
                            type="checkbox"
                            checked={formData.selectedPmps.includes(pmp.value)}
                            onChange={() => handlePmpSelection(pmp.value)}
                            className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          )}

          {/* Campaign Types */}
          <div className="px-8 py-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-2 mb-6">
              <Monitor className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Campaign Types</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Campaign Format <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.campaignFormat}
                  onChange={(e) => handleInputChange('campaignFormat', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                >
                  <option value="">Select Campaign Format</option>
                  {CAMPAIGN_FORMATS.map(format => (
                    <option key={format.value} value={format.value}>{format.label}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Display Target <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.displayTarget}
                  onChange={(e) => handleInputChange('displayTarget', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  disabled={availableDisplayTargets.length === 0}
                  required
                >
                  <option value="">Select Display Target</option>
                  {availableDisplayTargets.map(target => (
                    <option key={target.value} value={target.value}>{target.label}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {availableDisplayTargets.length === 0 
                    ? 'Select campaign format first'
                    : 'Targets available for your selected format'
                  }
                </p>
              </div>
            </div>
          </div>

          {/* Campaign Details */}
          <div className="px-8 py-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-2 mb-6">
              <Package className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Campaign Details</h2>
            </div>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Campaign name (support ticket & trafficking){' '}
                  {isVenueUser ? <span className="text-red-500">*</span> : <span className="text-gray-400 font-normal">(optional)</span>}
                </label>
                <input
                  type="text"
                  value={formData.campaignDisplayName}
                  onChange={(e) => handleInputChange('campaignDisplayName', e.target.value)}
                  required={isVenueUser}
                  maxLength={200}
                  placeholder={isVenueUser ? 'e.g. Thursday Special, Happy Hour, Spring Promo' : 'Leave blank to use format + display target as the campaign name'}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 transition-colors duration-200"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {isVenueUser
                    ? 'This exact name appears on your help desk ticket and should match what you use in other systems (e.g. Advangelists).'
                    : 'If set, this name is stored as the campaign title; otherwise the app uses “format — display target”.'}
                </p>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Creative asset targeting <span className="text-gray-400 font-normal">(optional)</span>
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                  Applies to your uploaded campaign asset. You can set these before attaching a file.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Target Gender
                    </label>
                    <select
                      value={formData.assetTargetGender}
                      onChange={(e) => handleInputChange('assetTargetGender', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="">Not specified</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="all">All Genders</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Age Block
                    </label>
                    <input
                      type="text"
                      value={formData.assetAgeBlock}
                      onChange={(e) => handleInputChange('assetAgeBlock', e.target.value)}
                      placeholder="e.g., 18-35, 25-44"
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Time of Day
                    </label>
                    <select
                      value={formData.assetTimeOfDay}
                      onChange={(e) => handleInputChange('assetTimeOfDay', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="">Not specified</option>
                      <option value="morning">Morning (6am-12pm)</option>
                      <option value="afternoon">Afternoon (12pm-6pm)</option>
                      <option value="evening">Evening (6pm-12am)</option>
                      <option value="night">Night (12am-6am)</option>
                      <option value="all-day">All Day</option>
                    </select>
                  </div>
                </div>
              </div>

              {!isVenueUser && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Campaign Term <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {CAMPAIGN_TERMS.map(term => (
                      <div
                        key={term.value}
                        className={`p-4 border-2 rounded-lg cursor-pointer transition-colors duration-200 ${
                          formData.campaignTerm === term.value
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                            : 'border-gray-300 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-400'
                        }`}
                        onClick={() => handleInputChange('campaignTerm', term.value)}
                      >
                        <div className="flex items-center space-x-3">
                          <input
                            type="radio"
                            checked={formData.campaignTerm === term.value}
                            onChange={() => handleInputChange('campaignTerm', term.value)}
                            className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                          />
                          <label className="font-medium text-gray-900 dark:text-white cursor-pointer">
                            {term.label}
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <Calendar className="w-4 h-4 inline mr-1" />
                    Requested Start Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.requestedStartDate}
                    onChange={(e) => handleInputChange('requestedStartDate', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <Calendar className="w-4 h-4 inline mr-1" />
                    {isVenueUser ? 'Requested End Date' : 'Calculated End Date'}
                  </label>
                  {isVenueUser ? (
                    <input
                      type="date"
                      value={formData.calculatedEndDate}
                      onChange={(e) => handleInputChange('calculatedEndDate', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  ) : (
                    <div className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-600 text-gray-700 dark:text-gray-300">
                      {formData.calculatedEndDate || 'Select start date and campaign term'}
                    </div>
                  )}
                  {!isVenueUser && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Automatically calculated based on start date and campaign term
                    </p>
                  )}
                </div>
              </div>

              {/* Campaign Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Campaign Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.campaignType}
                  onChange={(e) => handleInputChange('campaignType', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                >
                  <option value="">Select campaign type...</option>
                  {CAMPAIGN_TYPES.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              {/* Campaign Objective */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Campaign Objective <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.campaignObjective}
                  onChange={(e) => handleInputChange('campaignObjective', e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 transition-colors duration-200"
                  placeholder="What are you trying to accomplish with this campaign?"
                  required
                />
              </div>

              {/* When Should This Run */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  When Should This Run? <span className="text-red-500">*</span>
                </label>
                <div className="space-y-3">
                  {CAMPAIGN_TIMING.map(timing => (
                    <div key={timing.value} className="flex items-center space-x-3">
                      <input
                        type="radio"
                        id={`timing-${timing.value}`}
                        name="whenShouldRun"
                        value={timing.value}
                        checked={formData.whenShouldRun === timing.value}
                        onChange={(e) => handleInputChange('whenShouldRun', e.target.value)}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                        required
                      />
                      <label htmlFor={`timing-${timing.value}`} className="text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                        {timing.label}
                      </label>
                    </div>
                  ))}
                </div>
                {/* Specific days and/or hours: allow both together */}
                {(formData.whenShouldRun === 'specific-days' || formData.whenShouldRun === 'specific-hours') && (
                  <div className="mt-4 space-y-4">
                    {/* Days of week checkboxes with Select All */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Select Days of the Week</label>
                      <div className="flex items-center mb-2">
                        <input
                          type="checkbox"
                          id="selectAllDays"
                          checked={formData.daysOfWeek?.length === 7}
                          onChange={e => {
                            if (e.target.checked) {
                              handleInputChange('daysOfWeek', ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday']);
                            } else {
                              handleInputChange('daysOfWeek', []);
                            }
                          }}
                          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                        />
                        <label htmlFor="selectAllDays" className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">Select All</label>
                      </div>
                      <div className="flex flex-wrap gap-4">
                        {['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'].map(day => (
                          <label key={day} className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              checked={formData.daysOfWeek?.includes(day) || false}
                              onChange={e => {
                                const days = formData.daysOfWeek || [];
                                if (e.target.checked) {
                                  handleInputChange('daysOfWeek', [...days, day]);
                                } else {
                                  handleInputChange('daysOfWeek', days.filter(d => d !== day));
                                }
                              }}
                              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">{day}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    {/* Time pickers */}
                    <div className="flex flex-col md:flex-row md:items-center md:space-x-6 space-y-2 md:space-y-0">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Start Time</label>
                        <input
                          type="time"
                          value={formData.startTime || ''}
                          onChange={e => handleInputChange('startTime', e.target.value)}
                          className="border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">End Time</label>
                        <input
                          type="time"
                          value={formData.endTime || ''}
                          onChange={e => handleInputChange('endTime', e.target.value)}
                          className="border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Event-specific timing: show free-form text box */}
                {formData.whenShouldRun === 'event-timing' && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Timing Details <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={formData.timingDetails}
                      onChange={(e) => handleInputChange('timingDetails', e.target.value)}
                      rows={3}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 transition-colors duration-200"
                      placeholder="e.g., event nights only, see below, etc."
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Use this field to specify event-specific timing details.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Target Audience & Context (hide for venues) */}
          {!isVenueUser && (
          <div className="px-8 py-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-2 mb-6">
              <Target className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Target Audience & Context</h2>
            </div>
            
            <div className="space-y-6">
              {/* Venue Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Venue Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.venueType}
                  onChange={(e) => handleInputChange('venueType', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                >
                  <option value="">Select venue type...</option>
                  {VENUE_TYPES.map(venue => (
                    <option key={venue.value} value={venue.value}>{venue.label}</option>
                  ))}
                </select>
              </div>

              {/* Typical Audience */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Your Typical Audience <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.typicalAudience}
                  onChange={(e) => handleInputChange('typicalAudience', e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 transition-colors duration-200"
                  placeholder="Describe who you're trying to reach (age, interests, visit reasons, etc.)"
                  required
                />
              </div>

              {/* Key Message/Offer */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Key Message/Offer <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.keyMessage}
                  onChange={(e) => handleInputChange('keyMessage', e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 transition-colors duration-200"
                  placeholder="What's the main thing you want people to know or do?"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Special Requests</label>
                <textarea
                  value={formData.specialRequests}
                  onChange={(e) => handleInputChange('specialRequests', e.target.value)}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 transition-colors duration-200"
                  placeholder="Any specific requirements, targeting preferences, or additional information..."
                />
              </div>
            </div>
          </div>
          )}

          {/* Asset Upload or Content Creation Brief */}
          <div className="px-8 py-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-2 mb-4">
              <Upload className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Creative Assets</h2>
            </div>
            <div className="mb-4">
              <label className="inline-flex items-center space-x-2 text-sm text-gray-700 dark:text-gray-300">
                <input type="checkbox" className="w-4 h-4" checked={formData.needContentCreated} onChange={(e) => handleInputChange('needContentCreated', e.target.checked)} />
                <span>Need Content Created</span>
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Check this if you don’t have media assets. We’ll collect a brief instead.</p>
            </div>

            {/* Content Creation Services - shown only if Need Content Created is checked */}
            {formData.needContentCreated && (
              <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Content Creation Requirements</h3>
                
                {/* What services do you need */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    What services do you need? <span className="text-red-500">*</span>
                  </label>
                  <div className="space-y-2">
                    {CONTENT_SERVICES.map(service => (
                      <div key={service.value} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id={`service-${service.value}`}
                          checked={formData.contentServices.includes(service.value)}
                          onChange={(e) => {
                            const services = [...formData.contentServices];
                            if (e.target.checked) {
                              services.push(service.value);
                            } else {
                              const index = services.indexOf(service.value);
                              if (index > -1) services.splice(index, 1);
                            }
                            handleInputChange('contentServices', services);
                          }}
                          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                        />
                        <label htmlFor={`service-${service.value}`} className="text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                          {service.label}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Brand guidelines available */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Brand guidelines available? <span className="text-red-500">*</span>
                  </label>
                  <div className="flex space-x-6">
                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id="guidelines-yes"
                        name="brandGuidelines"
                        value="yes"
                        checked={formData.brandGuidelinesAvailable === 'yes'}
                        onChange={(e) => handleInputChange('brandGuidelinesAvailable', e.target.value)}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                      />
                      <label htmlFor="guidelines-yes" className="text-sm text-gray-700 dark:text-gray-300 cursor-pointer">Yes</label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id="guidelines-no"
                        name="brandGuidelines"
                        value="no"
                        checked={formData.brandGuidelinesAvailable === 'no'}
                        onChange={(e) => handleInputChange('brandGuidelinesAvailable', e.target.value)}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                      />
                      <label htmlFor="guidelines-no" className="text-sm text-gray-700 dark:text-gray-300 cursor-pointer">No</label>
                    </div>
                  </div>
                </div>

                {/* Content creation budget */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Content creation budget <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.contentCreationBudget}
                    onChange={(e) => handleInputChange('contentCreationBudget', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    required={formData.needContentCreated}
                  >
                    <option value="">Select budget range...</option>
                    {CONTENT_BUDGETS.map(budget => (
                      <option key={budget.value} value={budget.value}>{budget.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* Creative Specifications are always visible before upload */}
            <CreativeSpecs />

            {!formData.needContentCreated ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Campaign Assets <span className="text-red-500">*</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                      (One file per order)
                    </span>
                  </label>
                  <p className="-mt-1 mb-2 text-xs text-gray-500 dark:text-gray-400">
                    Accepted: {getAllowedFileTypes().description}.
                  </p>
                  {(!formData.files || formData.files.length === 0) ? (
                    <div 
                      className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors duration-200 ${
                        isDragOver 
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                          : 'border-gray-300 dark:border-gray-600 hover:border-blue-400'
                      }`}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                    >
                      <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <div className="mb-4">
                        <p className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Drop files here or click to browse
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {(() => {
                            const { description } = getAllowedFileTypes();
                            return formData.campaignFormat ? 
                              `Supported: ${description} (Max 50MB per file)` : 
                              'Select campaign format first to see supported file types';
                          })()}
                        </p>
                      </div>
                      <input
                        type="file"
                        onChange={handleFileUpload}
                        accept={getAllowedFileTypes().accept}
                        className="hidden"
                        id="file-upload"
                        disabled={!formData.campaignFormat}
                        required={!formData.needContentCreated && (!formData.files || formData.files.length === 0)}
                      />
                      <label
                        htmlFor="file-upload"
                        className={`inline-flex items-center px-4 py-2 font-medium rounded-lg cursor-pointer transition-colors duration-200 ${
                          !formData.campaignFormat 
                            ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                            : 'bg-blue-600 hover:bg-blue-700 text-white'
                        }`}
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Choose Files
                      </label>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {formData.files.map((file, index) => (
                          <div key={index} className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                            <div className="flex items-start space-x-3">
                              <FileText className="w-6 h-6 text-green-600 dark:text-green-400 flex-shrink-0 mt-1" />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                                  {file.name}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  {(file.size / 1024 / 1024).toFixed(1)} MB
                                </p>
                              </div>
                              <div className="flex items-center space-x-2">
                                <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                                <button
                                  type="button"
                                  onClick={() => removeFile(index)}
                                  className="p-1 hover:bg-red-100 dark:hover:bg-red-900/20 rounded text-red-600 hover:text-red-700 flex-shrink-0"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                            {ALLOW_IMAGE_CREATIVES && (file.type || '').startsWith('image/') && (
                              <div className="mt-3">
                                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                  Alt text <span className="text-red-500">*</span>
                                </label>
                                <input
                                  type="text"
                                  value={fileMetadata[file.name]?.alt || ''}
                                  onChange={(e) => {
                                    const alt = e.target.value;
                                    setFileMetadata(prev => ({
                                      ...prev,
                                      [file.name]: { ...(prev[file.name] || {}), alt }
                                    }));
                                  }}
                                  placeholder="Describe the image (accessibility / fallback text)"
                                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200"
                                />
                                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                  Required for image creatives.
                                </p>
                              </div>
                            )}
                          </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => document.getElementById('file-upload').click()}
                        className="w-full px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors duration-200"
                      >
                        <Plus className="w-4 h-4 inline mr-2" />
                        Replace File
                      </button>
                    </div>
                  )}
                  {resolutionWarning && (
                    <div className="mt-3 flex items-start gap-2 p-3 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700">
                      <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-amber-800 dark:text-amber-200">{resolutionWarning}</p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Content Type <span className="text-red-500">*</span></label>
                  <div className="flex items-center space-x-6">
                    <label className="inline-flex items-center space-x-2">
                      <input type="radio" name="contentType" value="video" checked={contentType === 'video'} onChange={(e) => setContentType(e.target.value)} />
                      <span className="text-sm text-gray-700 dark:text-gray-300">Video</span>
                    </label>
                    <label className="inline-flex items-center space-x-2">
                      <input type="radio" name="contentType" value="image" checked={contentType === 'image'} onChange={(e) => setContentType(e.target.value)} />
                      <span className="text-sm text-gray-700 dark:text-gray-300">Image</span>
                    </label>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Content Description / Storyboard <span className="text-red-500">*</span></label>
                  <textarea
                    rows={6}
                    value={contentDescription}
                    onChange={(e) => setContentDescription(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                    placeholder="Describe what you need (e.g., weekly special plate; menu item is the burrito and drink for $X). Include any branding, colors, or examples."
                  />
                </div>
              </div>
            )}
          </div>

          {/* Terms and Conditions */}
          <div className="px-8 py-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-3 mb-4">
              <input
                type="checkbox"
                id="termsAccepted"
                checked={formData.termsAccepted}
                onChange={(e) => handleInputChange('termsAccepted', e.target.checked)}
                className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                required
                disabled={!hasScrolledToBottom && !formData.termsAccepted}
              />
              <label htmlFor="termsAccepted" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                I accept the{' '}
                <button
                  type="button"
                  onClick={() => setShowTermsModal(true)}
                  className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline"
                >
                  Terms and Conditions
                </button>
                {' '}<span className="text-red-500">*</span>
              </label>
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
              By submitting an Insertion Order or providing advertising inventory to Context Networks, Inc., Media Company acknowledges that it has read, understood, and agrees to be bound by these Terms and Conditions.
            </p>
          </div>

          {/* Submit Button */}
          <div className="px-8 py-6 bg-gray-50 dark:bg-gray-700/50">
            <button
              type="submit"
              className="w-full font-semibold py-4 px-6 rounded-lg transition duration-200 shadow-lg hover:shadow-xl bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white cursor-pointer"
            >
              Submit Campaign Order
            </button>
          </div>
        </form>

        {/* Terms and Conditions Modal */}
        {showTermsModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[80vh] flex flex-col">
              <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Terms and Conditions
                </h3>
                <button
                  onClick={() => setShowTermsModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="flex-1 overflow-y-auto p-6" onScroll={handleTermsScroll}>
                <div className="prose dark:prose-invert max-w-none">
                  <pre className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300 font-sans leading-relaxed">
                    {TERMS_AND_CONDITIONS}
                  </pre>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => setShowTermsModal(false)}
                  className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    handleInputChange('termsAccepted', true);
                    setShowTermsModal(false);
                    setHasScrolledToBottom(false);
                  }}
                  disabled={!hasScrolledToBottom}
                  className={`px-6 py-2 rounded-lg font-medium transition-colors duration-200 ${
                    hasScrolledToBottom
                      ? 'bg-blue-600 hover:bg-blue-700 text-white cursor-pointer'
                      : 'bg-gray-400 text-gray-600 cursor-not-allowed'
                  }`}
                >
                  Accept Terms
                </button>
              </div>
              {!hasScrolledToBottom && (
                <div className="px-6 pb-2">
                  <p className="text-xs text-red-600 dark:text-red-400">
                    Please scroll to the bottom of the terms to accept
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Missing Fields Modal */}
        {showMissingFieldsModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full">
              <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
                  <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
                  Missing Required Fields
                </h3>
                <button
                  onClick={() => setShowMissingFieldsModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="p-6">
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  Please complete the following required fields before submitting:
                </p>
                <ul className="list-disc pl-5 space-y-1 text-sm text-gray-600 dark:text-gray-400">
                  {missingFields.map((field, index) => (
                    <li key={index}>{field}</li>
                  ))}
                </ul>
              </div>
              
              <div className="flex justify-end p-6 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => setShowMissingFieldsModal(false)}
                  className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderForm;